import { onMounted, onBeforeUnmount, ref } from "vue";

/**
 * A composable function that sets up a lazy loading mechanism using the IntersectionObserver API.
 * It triggers the provided callback function when the target element comes into view.
 *
 * @param callBack - The function to be called when the target element intersects with the viewport.
 * @param target - The target element to observe. It can be a CSS selector string or an Element. Defaults to ".product-list".
 * @param onlyOnce - If true, the callback will only be executed once.
 */
export function useLazy(
    callBack: () => void,
    target: string | Element = ".product-list",
    onlyOnce = false,
) {
    const lazyObserver = ref<IntersectionObserver | null>(null);
    let hasIntersected = false;

    onMounted(() => {
        if (!("IntersectionObserver" in window)) {
            callBack();
            return;
        }

        const elements =
            typeof target === "string"
                ? document.querySelectorAll(target) // Select all matching elements
                : [target];

        if (!elements || elements.length === 0) {
            return;
        }

        lazyObserver.value = new IntersectionObserver(
            (entries) => {
                // Iterate through all entries to find the correct one
                entries.forEach((entry, index) => {
                    if (entry.isIntersecting && shouldCallCallback()) {
                        hasIntersected = true;
                        callBack();

                        if (onlyOnce) {
                            lazyObserver.value?.disconnect();
                        }
                    }
                });
            },
            { threshold: [0] },
        );

        // Observe all elements
        elements.forEach((element) => {
            lazyObserver.value?.observe(element);
        });
    });

    const shouldCallCallback = () => {
        return !onlyOnce || !hasIntersected;
    };

    onBeforeUnmount(() => {
        if (lazyObserver.value) {
            lazyObserver.value.disconnect();
        }
    });
}
